import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import React, { FC, useMemo, useState } from 'react';
import { useEventCallback } from '@mui/material';
import {
  DoctorListDto,
  DoctorType,
  QueryFactory,
  SortDirection,
} from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
  useDictionaryByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import {
  RowClickEvent,
  SortOrder,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { CustomStore, DataSource } from 'devextreme/common/data';
import 'devextreme/dist/css/dx.light.css';
import { DataGrid } from '../../../common/dataGrid/DataGrid';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '../../../common/ui-kit/modal/useModal';

const DoctorTableKeys: { [dictionary: string]: string } = {
  id: 'id',
  name: 'name',
  surname: 'surname',
  specialty: 'specialty',
  active: 'active',
  subtenants: 'subtenants',
  actions: 'actions',
};

const pageSize = 100;

type Props = {
  getRows: (
    skip?: number,
    take?: number,
    sortingDirection?: SortDirection,
  ) => any;
  doctorType: DoctorType;
  queryParams: any;
};

export const DoctorsTable: FC<Props> = React.memo(function DoctorsTable(props) {
  const i18next = useScopedTranslation('doctors.list');
  const i18nextForm = useScopedTranslation('doctors.form');
  const forms = useGlobalForms();
  const modals = useModal();
  const queryClient = useQueryClient();

  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const doctorDictionary = useDictionaryByTitle(CustomDictionaryName.Doctors);

  const tableKey = useMemo(
    () =>
      `search=${props.queryParams.search}_isOnlyActive=${props.queryParams.isOnlyActive}_sortOrder=${sortOrder}`,
    [props.queryParams, sortOrder],
  );

  const { data: specialties = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Specialties,
  );

  const columns = useMemo(
    () => [
      {
        key: DoctorTableKeys.name,
        dataField: DoctorTableKeys.surname,
        caption: i18next.t(`columns.${DoctorTableKeys.name}` as any),
        cellRender: (cell: any) => getDoctorFullName(cell.data),
        allowSorting: true,
        defaultSortOrder: 'asc' as SortOrder,
        onSortOrderChange: (order: SortOrder | undefined) =>
          setSortOrder(order || 'asc'),
      },
      {
        key: DoctorTableKeys.specialty,
        caption: i18next.t(`columns.${DoctorTableKeys.specialty}` as any),
        cellRender: (cell: any) =>
          specialties.find((x) => x.id === cell.data.specialtyId)?.value ?? '',
      },
      {
        key: DoctorTableKeys.active,
        caption: i18next.t(`columns.${DoctorTableKeys.active}` as any),
        cellRender: (cell: any) =>
          i18next.t(`statuses.${cell.data.active ? 'active' : 'not_active'}`),
      },
      {
        key: DoctorTableKeys.actions,
        caption: '',
        width: '60px',
        cellRender: (cell: any) => (
          <div onClick={(event: any) => event.stopPropagation()}>
            <TableDotMenu
              options={[
                {
                  title: i18next.t('options.edit_button'),
                  onClick: () => openEditing(cell),
                },
                {
                  title: i18next.t('options.delete_button'),
                  onClick: () => onDelete(cell.data.id),
                  isDeleteItem: true,
                  isUnderLine: true,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [specialties],
  );

  const dataSource = useMemo(
    () =>
      new DataSource({
        store: new CustomStore({
          key: 'id',
          load: async (loadOptions) => {
            let desc: SortDirection = SortDirection.Asc;

            if (loadOptions.sort) {
              const sort: any = Array.isArray(loadOptions.sort)
                ? loadOptions.sort[0]
                : loadOptions.sort;

              desc = sort.desc ? SortDirection.Desc : SortDirection.Asc;
            }

            const skip = loadOptions.skip ?? 0;
            const take = loadOptions.take ?? pageSize;

            const result = await props.getRows(skip, take, desc);

            return {
              data: result.data,
              totalCount: result.totalCount,
            };
          },
        }),
      }),
    [props.queryParams],
  );

  const onDelete = useEventCallback(async (id: number) => {
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18nextForm.t('confirmation_delete_doctor_title'),
      text: i18nextForm.t('confirmation_delete_doctor_text'),
      okButtonText: i18nextForm.t('confirmation_delete_doctor_button'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.DoctorClient.removeDoctor(id);
        dataSource.reload();
      } catch (error: any) {
        await modals.showError({ text: error.message });
      }
    }
  });

  const openEditing = useEventCallback((e: RowClickEvent) => {
    forms.openDoctorEditing(props.doctorType, e.data.id, true, () =>
      dataSource.reload(),
    );
  });

  return (
    <DataGrid
      key={tableKey}
      keyExpr="id"
      columns={columns}
      onRowClick={openEditing}
      scrolling={{
        mode: 'virtual',
      }}
      remoteOperations={{ paging: true, sorting: true }}
      paging={{ pageSize: pageSize }}
      dataSource={dataSource}
      enableScrollTop
    />
  );
});
